/**
 * FeatureTeaserGrid — server component (no client state)
 *
 * 7 plugin cards for the ɳSentry landing page.
 * Baseline bundle: uptime-monitor, status-page, incident-mgmt,
 * alert-router, slo-tracker, synthetic-monitor, rum.
 */

import type { ReactNode } from 'react'

interface Plugin {
  title: string
  description: string
  port: number
  icon: ReactNode
}

const PLUGINS: Plugin[] = [
  {
    title: 'Uptime Monitor',
    description:
      'HTTP and TCP uptime checks with configurable intervals. Triggers incidents automatically when thresholds breach. No external dependency — runs entirely on your nSelf server.',
    port: 3831,
    icon: (
      <svg
        className="h-6 w-6"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
        />
      </svg>
    ),
  },
  {
    title: 'Status Page',
    description:
      'Public and private status pages for your services. Custom domain, component groups, incident history, subscriber notifications. Hosted on your infra, not ours.',
    port: 3832,
    icon: (
      <svg
        className="h-6 w-6"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M3.75 3v11.25A2.25 2.25 0 0 0 6 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0 1 18 16.5h-2.25m-7.5 0h7.5m-7.5 0-1 3m8.5-3 1 3m0 0 .5 1.5m-.5-1.5h-9.5m0 0-.5 1.5M9 11.25v1.5M12 9v3.75m3-6v6"
        />
      </svg>
    ),
  },
  {
    title: 'Incident Management',
    description:
      'Full incident lifecycle: open, escalate, resolve, post-mortem. Assign owners, track timelines, link alerts to incidents. Integrates with the alert router for automatic creation.',
    port: 3833,
    icon: (
      <svg
        className="h-6 w-6"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
        />
      </svg>
    ),
  },
  {
    title: 'Alert Router',
    description:
      'Multi-channel alert routing to Slack, email, PagerDuty, and webhooks. Route rules by severity, component, or team. Silence windows for planned maintenance.',
    port: 3834,
    icon: (
      <svg
        className="h-6 w-6"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0"
        />
      </svg>
    ),
  },
  {
    title: 'SLO Tracker',
    description:
      'Define service-level objectives and track error budgets over 7, 28, and 90-day rolling windows. Dashboard surfaces SLO compliance and burn rate before budget exhausts.',
    port: 3835,
    icon: (
      <svg
        className="h-6 w-6"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M7.5 14.25v2.25m3-4.5v4.5m3-6.75v6.75m3-9v9M6 20.25h12A2.25 2.25 0 0 0 20.25 18V6A2.25 2.25 0 0 0 18 3.75H6A2.25 2.25 0 0 0 3.75 6v12A2.25 2.25 0 0 0 6 20.25Z"
        />
      </svg>
    ),
  },
  {
    title: 'Synthetic Monitor',
    description:
      'Scripted browser checks that simulate real user flows — login, checkout, form submission. Run on schedule, alert on failure. Catch broken flows before your users do.',
    port: 3836,
    icon: (
      <svg
        className="h-6 w-6"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M9 17.25v1.007a3 3 0 0 1-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0 1 15 18.257V17.25m6-12V15a2.25 2.25 0 0 1-2.25 2.25H5.25A2.25 2.25 0 0 1 3 15V5.25m18 0A2.25 2.25 0 0 0 18.75 3H5.25A2.25 2.25 0 0 0 3 5.25m18 0H3"
        />
      </svg>
    ),
  },
  {
    title: 'Real User Monitoring',
    description:
      'Capture page load times, Core Web Vitals, and JS errors from real browsers. First-party only — data stays in your Postgres. No third-party script injection.',
    port: 3837,
    icon: (
      <svg
        className="h-6 w-6"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z"
        />
      </svg>
    ),
  },
]

export function FeatureTeaserGrid() {
  return (
    <section
      aria-labelledby="plugins-heading"
      className="border-t border-brand-subtle bg-brand-surface/30 py-20"
    >
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <h2
          id="plugins-heading"
          className="mb-3 text-center text-3xl font-bold"
        >
          7 plugins, one bundle
        </h2>
        <p className="mb-12 text-center text-brand-muted">
          Every plugin in the baseline bundle. Self-hosted, no data leaves your server.
        </p>
        <ul
          role="list"
          className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3"
        >
          {PLUGINS.map((p) => (
            <li
              key={p.title}
              className="rounded-xl border border-brand-subtle bg-brand-bg p-6"
            >
              <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-sky-50 dark:bg-sky-950 text-sky-700 dark:text-sky-400">
                <>{p.icon}</>
              </div>
              <h3 className="mb-2 font-semibold text-brand-ink">{p.title}</h3>
              <p className="text-sm text-brand-muted">{p.description}</p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  )
}
